import {
  HttpClient,
  HttpContext,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { CursorPagination, Pagination } from '~core/models/core.models';
import {
  CreateInvoiceParams,
  Invoice,
  LineItem,
  ListInvoiceRequestParams,
  ListInvoiceV2RequestParams
} from '~core/models/invoice.models';
import { ApiConstantsService } from './api-constants.service';
import { SalesPoint } from '~core/models/sales-point.models';
import { Establishment } from '~core/models/establishment.model';
import { getInvoicePaymentMethods } from '~core/utils/payment';

@Injectable()
export class InvoiceService {
  private _apiConstantsService = inject(ApiConstantsService);
  private _httpClient = inject(HttpClient);

  get apiUrl(): string {
    return this._apiConstantsService.invoicesUrl;
  }

  get coreApiURL(): string {
    return this._apiConstantsService.coreManagerMicroservice;
  }

  private _normalizeInvoice(invoice: Invoice): Invoice {
    return {
      ...invoice,
      paymentMethods: getInvoicePaymentMethods(invoice)
    };
  }

  read(invoiceId: string): Observable<Invoice> {
    return this._httpClient
      .get<Invoice>(`${this.apiUrl}/${invoiceId}`)
      .pipe(map(invoice => this._normalizeInvoice(invoice)));
  }

  list(
    params?: ListInvoiceRequestParams & {
      [key: string]: any;
    }
  ): Observable<Pagination<Invoice>> {
    const requestParams = {
      ...params,
      paymentMethods: Array.isArray(params?.paymentMethods)
        ? params.paymentMethods.join(',')
        : params?.paymentMethods,
      sort: params?.sort || 'createdAt',
      desc: params?.desc || 'createdAt'
    } as Record<string, any>;

    return this._httpClient
      .get<Pagination<Invoice>>(this.apiUrl, {
        params: requestParams
      })
      .pipe(
        map(res => ({
          ...res,
          items: res.items.map(invoice => ({
            ...this._normalizeInvoice(invoice),
            clientSideMetas: {
              salePoint$: this._httpClient.get(
                `${this._apiConstantsService.salesPointsUrl}/${invoice.salesPointId}`
              ) as Observable<SalesPoint>,
              establishment$: this._httpClient.get(
                `${this._apiConstantsService.establishmentsUrl}/${invoice.establishmentId}`
              ) as Observable<Establishment>
            }
          }))
        }))
      );
  }

  listV2(
    params?: ListInvoiceV2RequestParams
  ): Observable<CursorPagination<Invoice>> {
    const {
      page: _page,
      pageNumber: _pageNumber,
      fullyItems: _fullyItems,
      paymentMethods,
      sort,
      desc,
      ...rest
    } = params ?? {};
    const requestParams = {
      ...rest,
      paymentMethods: Array.isArray(paymentMethods)
        ? paymentMethods.join(',')
        : paymentMethods,
      ...(sort ? { sort } : {}),
      ...(desc ? { desc } : {})
    };

    return this._httpClient
      .get<
        Omit<CursorPagination<Invoice>, 'mode'>
      >(this._apiConstantsService.invoicesV2Url, { params: requestParams as Record<string, string | number | boolean> })
      .pipe(
        map(res => ({
          ...res,
          mode: 'cursor' as const,
          items: res.items.map(invoice => ({
            ...this._normalizeInvoice(invoice),
            clientSideMetas: {
              salePoint$: this._httpClient.get(
                `${this._apiConstantsService.salesPointsUrl}/${invoice.salesPointId}`
              ) as Observable<SalesPoint>,
              establishment$: this._httpClient.get(
                `${this._apiConstantsService.establishmentsUrl}/${invoice.establishmentId}`
              ) as Observable<Establishment>
            }
          }))
        }))
      );
  }

  create(data: CreateInvoiceParams): Observable<Invoice> {
    return this._httpClient
      .post<Invoice>(this.apiUrl, {
        ...data,
        useStateFormViolationHandler: true
      })
      .pipe(map(invoice => this._normalizeInvoice(invoice)));
  }

  update(id: string, data: Partial<CreateInvoiceParams>): Observable<Invoice> {
    return this._httpClient
      .patch<Invoice>(`${this.apiUrl}/${id}`, {
        ...data,
        useStateFormViolationHandler: true
      })
      .pipe(map(invoice => this._normalizeInvoice(invoice)));
  }

  addLineItem(invoiceId: string, data: LineItem): Observable<LineItem> {
    return this._httpClient.post<LineItem>(
      `${this.apiUrl}/${invoiceId}/line-items`,
      {
        ...data,
        useStateFormViolationHandler: true
      }
    );
  }

  createFromBulk(
    invoiceId: string,
    data: { lineItems: LineItem[] }
  ): Observable<LineItem> {
    return this._httpClient.post<LineItem>(
      `${this.apiUrl}/${invoiceId}/line-items/from-bulk`,
      {
        ...data,
        useStateFormViolationHandler: true
      }
    );
  }

  updateLineItem(
    invoiceId: string,
    lineId: string,
    data: LineItem
  ): Observable<LineItem> {
    return this._httpClient.patch<LineItem>(
      `${this.apiUrl}/${invoiceId}/line-items/${lineId}`,
      {
        ...data,
        useStateFormViolationHandler: true
      }
    );
  }

  import(file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);

    return this._httpClient.post<void>(
      `${this.coreApiURL}/create-invoices-from-excel`,
      formData
    );
  }

  deleteLineItem(invoiceId: string, lineId: string): Observable<unknown> {
    return this._httpClient.delete<LineItem>(
      `${this.apiUrl}/${invoiceId}/line-items/${lineId}`
    );
  }

  requestCertification(
    invoiceId: string,
    context?: HttpContext
  ): Observable<unknown> {
    return this._httpClient.get<unknown>(
      `${this.apiUrl}/${invoiceId}/certification/request`,
      context ? { context } : {}
    );
  }

  delete(invoiceId: string): Observable<unknown> {
    return this._httpClient.delete<unknown>(`${this.apiUrl}/${invoiceId}`);
  }

  downloadInvoice(
    invoiceId: string,
    currency?: string
  ): Observable<HttpResponse<ArrayBuffer>> {
    let params = new HttpParams();

    if (currency) {
      params = params.set('currency', currency.toUpperCase());
    }

    return this._httpClient.get(`${this.apiUrl}/${invoiceId}/download`, {
      params,
      responseType: 'arraybuffer',
      observe: 'response'
    });
  }

  publicSearch(invoiceIdReference: string): Observable<Invoice> {
    return this._httpClient
      .get<Invoice>(
        `${this._apiConstantsService.invoiceManagerMicroservice}/public-search/invoices/${invoiceIdReference}`,
        {
          params: {
            skipJwtInterceptor: true
          }
        }
      )
      .pipe(map(invoice => this._normalizeInvoice(invoice)));
  }
}
